import React, { useEffect, useState, useMemo, useRef } from 'react';
import { formatUserDisplayName } from '@/lib/user-display';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { SectionLayout } from '@/components/section-layout';
import { useOpenEntity } from '@/hooks/useOpenEntity';
import { NavigationService } from '@/services/navigationService';
import { fetchEntity } from '@/lib/entity-api';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { useEntityComments } from '@/hooks/useEntityComments';
import { getSession as getCachedSession } from '@/lib/auth-session';
import { useLocation } from 'react-router-dom';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { GenericTag } from '@/styles/GenericTag';
import { format } from 'date-fns';
import { AlertCircle, Calendar, Clock, FileText, MessageSquare, Paperclip, MoreHorizontal, Trash2, Copy, Download, Upload } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TodoForm } from '@/features/project-management/todos/TodoForm';
import { GenericAttachmentsSection } from '@/styles/GenericAttachmentsSection';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import type { GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { uploadTaskAttachments, fetchTaskAttachments as fetchTaskAttachmentsShared } from '@/features/project-management/todos/store';
import { trackTodoCreated, trackProjectManagementAction, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
// Types defined locally since @/types/todo doesn't exist


interface Task {
  id: string;
  todo_id?: string | null;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_date: string;
  due_time: string;
  project_id: string;
  assignee_id: string;
  created_by: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  reminder_times: string | null;
  assignee?: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
  project?: {
    id: string;
    name: string;
  };
  attachments: { count: number }[];
  comments: { count: number }[];
  attachment_count: number;
}

interface TaskAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_url: string;
  created_at: string;
  created_by: string;
}


type FileAttachment = GenericAttachment;

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  // Parse date-only strings (YYYY-MM-DD) as local dates to avoid timezone issues
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return new Intl.DateTimeFormat('default', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  }
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('default', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

const formatTime = (timeStr: string) => {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  return new Intl.DateTimeFormat('default', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(date);
};

export function TodosPage() {
  const { selectedProject } = useProject();
  const { companyId, user } = useUser();
  const { shouldOpen, entityId } = useOpenEntity('task');
  const location = useLocation();
  // The list: ONE server-driven query (project + search + sort + page) -- search, sort and paging run in the database
  // over every to-do the user may see; the counters come with each row (hooks/useEntityList.ts).
  const list = useEntityList<any, Task>({
    entityType: 'task',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    includeCounts: true,
    defaultSort: { columnKey: 'todo_id', direction: 'asc', sortKey: 'todo_id' },
    mapRow: (row) => ({
      ...row,
      attachments: [{ count: row.attachment_count || 0 }],
      comments: [{ count: row.comment_count || 0 }],
    }),
  });
  const tasks = list.items;
  const setTasks = list.setItems;
  // Latest values for effects that run on navigation only (read when they run, never a reason to re-run them).
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;
  const fetchTaskAttachmentsRef = useRef<(taskId: string) => void>(() => {});
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const { projects, users } = useCompanyLookups(!!companyId);
  const [isNewTodoOpen, setIsNewTodoOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [todoForm, setTodoForm] = useState({
    title: '',
    description: '',
    assignee: '',
    dueDate: '',
    dueTime: '',
    priority: 'medium',
    status: 'pending',
    comments: '',
    projectId: '',
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  // Comments of the task being viewed/edited: the shared hook reads and writes them through the API.
  const { comments, addComment, deleteComment } = useEntityComments('task', editingTask?.id || selectedTask?.id);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [originalEditingTask, setOriginalEditingTask] = useState<Task | null>(null);
  // The cached auth session the forms receive.
  const session = getCachedSession();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [taskAttachments, setTaskAttachments] = useState<Record<string, FileAttachment[]>>({});
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = new Date();
        time.setHours(hour, minute, 0);
        const formattedTime = time.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit', 
          hour12: true 
        });
        options.push({
          label: formattedTime,
          value: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        });
      }
    }
    return options;
  }, []);

  const reminderOptions = [
    { label: '15 minutes before', value: '15' },
    { label: '30 minutes before', value: '30' },
    { label: '1 hour before', value: '60' },
    { label: '2 hours before', value: '120' },
    { label: '4 hours before', value: '240' },
    { label: '1 day before', value: '1440' },
    { label: '2 days before', value: '2880' },
    { label: '3 days before', value: '4320' },
  ];

  // Check if we should open the form from navigation
  useEffect(() => {
    if (location.state?.openForm) {
      setIsNewTodoOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check if we should open a specific task from navigation
  useEffect(() => {
    if (!shouldOpen || !entityId || !list.hasLoaded) return;
    // Clear the state to prevent reopening on refresh
    NavigationService.clearOpenState();
    // The record may not be on the page that is showing: ask the API for it, then open it in edit mode.
    const loaded = tasksRef.current.find((t) => t.id === entityId);
    (loaded ? Promise.resolve(loaded) : fetchEntity<any>('task', entityId, { includeCounts: true }))
      .then((task) => {
        setEditingTask(task);
        setOriginalEditingTask(task);
        setIsEditModalOpen(true);
        fetchTaskAttachmentsRef.current(entityId);
      })
      .catch((error) => console.error('Error opening to-do:', error));
  }, [shouldOpen, entityId, list.hasLoaded]);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  // Loads the quick-view dialog's attachments when it opens (its comments come from useEntityComments).
  useEffect(() => {
    if (selectedTask?.id) {
      fetchTaskAttachments(selectedTask.id);
    }
  }, [selectedTask?.id]);

  // Map priority to GenericTag priority types (urgent -> high)
  const mapPriorityToTag = (priority: string): 'low' | 'medium' | 'high' => {
    switch (priority.toLowerCase()) {
      case 'low':
        return 'low';
      case 'medium':
        return 'medium';
      case 'high':
      case 'urgent':
        return 'high'; // Map urgent to high
      default:
        return 'medium';
    }
  };

  // Map todo statuses to GenericTag status types
  const mapTodoStatusToTag = (status: string): 'draft' | 'sent' | 'approved' | 'rejected' | 'pending' | 'in_progress' | 'completed' | 'cancelled' => {
    switch (status.toLowerCase()) {
      case 'draft':
        return 'draft';
      case 'sent':
        return 'sent';
      case 'pending':
        return 'pending'; // Yellow
      case 'in_progress':
        return 'in_progress'; // Green
      case 'completed':
        return 'completed'; // Blue
      case 'on_hold':
        return 'pending'; // Yellow for on hold
      case 'cancelled':
        return 'cancelled'; // Use cancelled status with dedicated red color
      default:
        return 'draft';
    }
  };

  // Format status text is now handled by GenericTag component

  // getStatusColor removed - now using GenericTag component

  const handleCreateTodo = async (formData: any) => {
    try {
      console.log('Starting todo creation...');
      if (!companyId || !user?.id) {
        setError('No company ID or user ID found');
        return;
      }
      console.log('User found:', user.id);

      // Validate required fields
      if (!formData.title || !formData.project_id) {
        const missing = [];
        if (!formData.title) missing.push('Title');
        if (!formData.project_id) missing.push('Project');
        const errorMsg = `Please fill in all required fields: ${missing.join(', ')}`;
        console.log('Validation failed:', errorMsg);
        setError(errorMsg);
        return;
      }

      // Prepare API payload
      const apiPayload = {
        title: formData.title?.trim?.() ?? formData.title,
        description: formData.description?.trim?.() ?? formData.description,
        project_id: formData.project_id,
        assignee_id: formData.assignee_id || null,
        status: formData.status,
        priority: formData.priority,
        due_date: formData.due_date || null,
        due_time: formData.due_time || null,
        reminder_times: formData.reminder_times || null,
        user_id: user.id,        // Required for change tracking
        company_id: companyId    // Required for change tracking
      };

      console.log('Creating todo via API with data:', apiPayload);

      const response = await fetch(getApiUrl('api/entities/task'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error creating todo:', errorData);
        throw new Error(errorData.error || 'Failed to create todo');
      }

      const result = await response.json();
      const data = result.data; // Extract data from API response

      console.log('Todo created successfully via API:', data);

      // Track successful todo creation with PostHog
      trackTodoCreated({
        project_id: formData.project_id,
        todo_id: data.id,
        title: formData.title,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        due_date: formData.due_date,
        assignee_id: formData.assignee_id,
        company_id: companyId,
        created_by: user.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'todo_creation',
        success: true
      });

      // If files were queued in the form, upload them now that we have an ID
      if (data?.id && Array.isArray(formData.attachmentsFiles) && formData.attachmentsFiles.length > 0) {
        try {
          const { uploadTaskAttachments } = await import('@/features/project-management/todos/store');
          const uploaded = await uploadTaskAttachments(data.id, formData.attachmentsFiles, companyId, user.id);
          console.log('Queued attachments uploaded after create', { count: uploaded.length });
        } catch (uploadAfterCreateErr) {
          console.error('Error uploading queued attachments after create:', uploadAfterCreateErr);
        }
      }
      list.reload();
      setIsNewTodoOpen(false);
      setError(null);
    } catch (error: any) {
      console.error('Error creating todo:', error);
      
      // Track failed todo creation with PostHog
      trackFormSubmission({
        form_type: 'todo_creation',
        success: false,
        error_message: error.message || 'Failed to create todo'
      });

      setError(error.message || 'Failed to create todo');
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCreateTodo(todoForm);
  };

  const handleRowSelect = (taskId: string) => {
    setSelectedRows(prev => {
      if (prev.includes(taskId)) {
        return prev.filter(id => id !== taskId);
      }
      return [...prev, taskId];
    });
  };

  const handleRowClick = async (task: Task) => {
    setEditingTask(task);
    setOriginalEditingTask(task);
    setIsEditModalOpen(true);

    // Comments reload by themselves (useEntityComments follows the open task); files are fetched here.
    await fetchTaskAttachments(task.id);
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    // BUG-21 fix (2026-09-19): same UUID-fallback issue as getAssigneeName
    // just below -- a project outside the current fetch scope must never
    // surface its raw id to the user.
    return project?.name || 'Unknown Project';
  };

  const getAssigneeName = (assigneeId: string | null) => {
    if (!assigneeId) return 'Unassigned';
    const user = users.find(u => u.id === assigneeId);
    // BUG-21 fix (2026-09-19): never fall back to the raw UUID -- `users`
    // is scoped to currently-active company members (fetchUsers filters
    // is_active=true), so an assignee who was deactivated/removed after
    // being assigned won't be found here. Previously this returned the
    // bare assigneeId string, exposing a UUID directly in the UI.
    return user?.full_name || 'Unknown User';
  };

  const handleSaveChanges = async (formData: any) => {
    try {
      if (!editingTask) return;

      if (!companyId || !user?.id) {
        setError('No company ID or user ID found');
        return;
      }

      const updateData = {
        title: formData.title,
        description: formData.description,
        project_id: formData.project_id,
        assignee_id: formData.assignee_id || null,
        status: formData.status,
        priority: formData.priority,
        due_date: formData.due_date,
        due_time: formData.due_time,
        reminder_times: formData.reminder_times,
        user_id: user.id,        // Required for change tracking
        company_id: companyId    // Required for change tracking
      };

      console.log('Updating todo via API with data:', updateData);

      const response = await fetch(getApiUrl(`api/entities/task/${editingTask.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error updating todo:', errorData);
        throw new Error(errorData.error || 'Failed to update todo');
      }

      const result = await response.json();
      const data = result.data; // Extract data from API response

      console.log('Todo updated successfully via API:', data);

      // Update the tasks list with the edited task
      setTasks(tasks.map(t => t.id === editingTask.id ? data : t));
      
      // Refresh attachments (comments are reloaded by the shared hook)
      fetchTaskAttachments(editingTask.id);
      
      setIsEditModalOpen(false);
      setEditingTask(null);
      setOriginalEditingTask(null);
    } catch (error: any) {
      console.error('Error updating task:', error);
      setError(error.message || 'Failed to update task');
    }
  };

  const handleCloseEditModal = () => {
    if (JSON.stringify(editingTask) !== JSON.stringify(originalEditingTask)) {
      setIsDiscardModalOpen(true);
    } else {
      setIsEditModalOpen(false);
      setEditingTask(null);
      setOriginalEditingTask(null);
      // Refresh comments when closing the modal
      if (editingTask) {
        }
    }
  };

  const handleDiscardChanges = () => {
    setIsDiscardModalOpen(false);
    setIsEditModalOpen(false);
    setEditingTask(null);
    setOriginalEditingTask(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const fetchTaskAttachments = async (taskId: string) => {
    try {
      const signed = await fetchTaskAttachmentsShared(taskId, { ascending: false });

      setTaskAttachments(prev => ({
        ...prev,
        [taskId]: signed || []
      }));

      // Update the task's attachment count
      setTasks(prev => prev.map(task => 
        task.id === taskId 
          ? { ...task, attachment_count: signed?.length || 0 }
          : task
      ));
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  fetchTaskAttachmentsRef.current = fetchTaskAttachments;

  // 404 fix (2026-09-27): this used to ALSO call
  // `DELETE /api/entities/task_attachment/:id` itself -- but TodoForm's
  // own handleDeleteFile (useAttachmentStaging's deleteFn, see
  // todos/store.ts's deleteTaskAttachment) already performs the real
  // deletion BEFORE calling this callback. That made every delete hit the
  // endpoint twice; the second call always 404'd because the row was
  // already soft-deleted by the first. This callback's only job is to
  // sync this page's own cached attachment list/count, same as
  // Specifications' page.tsx already did correctly (see its own
  // handleDeleteAttachment) -- never call the network again here.
  const handleDeleteAttachment = (taskId: string, attachmentId: string) => {
    setTaskAttachments(prev => ({
      ...prev,
      [taskId]: (prev[taskId] || []).filter(att => att.id !== attachmentId)
    }));

    setTasks(prev => prev.map(task =>
      task.id === taskId
        ? { ...task, attachment_count: Math.max(0, (task.attachment_count || 0) - 1) }
        : task
    ));
  };

  // Handle copy selected todos
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one todo to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy todos');
      return;
    }

    try {
      // Get selected tasks
      const selectedTasks = tasks.filter(t => selectedRows.includes(t.id));

      // Copy each selected task - let API generate todo_id automatically
      for (const task of selectedTasks) {
        const response = await fetch(getApiUrl('api/entities/task'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId,
            // Don't include todo_id - let API generate it
            project_id: task.project_id,
            assignee_id: task.assignee_id,
            title: task.title,
            description: task.description || null,
            status: 'pending', // Always set to pending for copies
            priority: task.priority,
            due_date: task.due_date || null,
            due_time: task.due_time || null,
            reminder_times: task.reminder_times || null,
            // completed_at is not included (will be null)
            created_by: user.id, // Current user
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error copying todo:', errorData);
          continue;
        }
      }

      // Refresh tasks list
      list.reload();
      setSelectedRows([]);
      alert(`Successfully copied ${selectedTasks.length} todo(s).`);
    } catch (error) {
      console.error('Error copying todos:', error);
      alert('Error copying todos. Please try again.');
    }
  };

  // Handle delete selected todos
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one todo to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} todo(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete todos');
      return;
    }

    try {
      for (const taskId of selectedRows) {
        const response = await fetch(getApiUrl(`api/entities/task/${taskId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error deleting todo:', errorData);
          continue;
        }
      }

      // Refresh tasks list
      list.reload();
      setSelectedRows([]);
      alert(`Successfully deleted ${selectedRows.length} todo(s).`);
    } catch (error) {
      console.error('Error deleting todos:', error);
      alert('Error deleting todos. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY to-do of the
  // current project + search, read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'todos', 'todos');

  // Define table columns
  const columns: GenericTableColumn<Task>[] = [
    {
      key: 'todo_id',
      header: 'Todo ID',
      width: '6.5rem',
      sortable: true,
      sortKey: 'todo_id',
      render: (row) => <span className="font-medium">{row.todo_id || '-'}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      width: '12.5rem', // 200px = 12.5rem
      sortable: true,
      sortKey: 'title',
      render: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'project_id',
      header: 'Project',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'project',
      render: (row) => getProjectName(row.project_id),
    },
    {
      key: 'assignee_id',
      header: 'Assignee',
      width: '9.375rem', // 150px = 9.375rem
      sortable: true,
      sortKey: 'assignee',
      render: (row) => getAssigneeName(row.assignee_id),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8.125rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag 
          type="status" 
          value={mapTodoStatusToTag(row.status)}
        />
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      width: '6.875rem',
      sortable: true,
      sortKey: 'priority',
      render: (row) => (
        <GenericTag 
          type="priority" 
          value={mapPriorityToTag(row.priority)}
        />
      ),
    },
    {
      key: 'due_date',
      header: 'Due Date',
      width: '8.125rem',
      sortable: true,
      sortKey: 'due_date',
      render: (row) => row.due_date ? formatDate(row.due_date) : '-',
    },
    {
      key: 'created_at',
      header: 'Created',
      width: '8.125rem',
      sortable: true,
      sortKey: 'created_at',
      render: (row) => formatDate(row.created_at),
    },
    {
      key: 'attachments',
      header: 'Attachments',
      width: '6.875rem',
      align: 'center' as const,
      render: (row) => row.attachments?.[0]?.count || 0,
    },
    {
      key: 'comments',
      header: 'Comments',
      width: '6.25rem',
      align: 'center' as const,
      render: (row) => row.comments?.[0]?.count || 0,
    },
  ];

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
  ];

  if (!list.hasLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5 mb-4">
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
            <div className="w-2 h-2 bg-gray-900 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <p className="text-sm text-gray-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <SectionLayout title="To-Do's">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_todo_button', { context: 'project_management' });
                setIsNewTodoOpen(true);
              }}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
        />

        {isNewTodoOpen && (
            <TodoForm
              mode="create"
              projects={projects}
              users={users}
              onSubmit={handleCreateTodo}
              onCancel={() => setIsNewTodoOpen(false)}
              session={session}
            />
        )}

        <Dialog open={isDiscardModalOpen} onOpenChange={setIsDiscardModalOpen}>
          <DialogContent className="sm:max-w-[25rem]">
            <DialogHeader>
              <DialogTitle>Discard Changes?</DialogTitle>
            </DialogHeader>
            <div className="flex justify-end gap-4 mt-4">
              <Button type="button" variant="outline" onClick={() => setIsDiscardModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDiscardChanges}
              >
                Discard Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {isEditModalOpen && editingTask && (
              <TodoForm
                mode="edit"
                initialData={editingTask}
                projects={projects}
                users={users}
                onSubmit={handleSaveChanges}
                onCancel={handleCloseEditModal}
                comments={comments}
                onAddComment={addComment}
                onDeleteComment={deleteComment}
                session={session}
                attachments={taskAttachments[editingTask.id] || []}
                onDeleteAttachment={(id) => handleDeleteAttachment(editingTask.id, id)}
              />
            )}

        <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTask?.title}</DialogTitle>
            </DialogHeader>
            {selectedTask && (
              <div className="space-y-6">
                {/* Task Details */}
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="text-secondary">Description</div>
                      <div className="table-text">{selectedTask.description || 'No description'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <GenericTag 
                        type="priority" 
                        value={mapPriorityToTag(selectedTask.priority)}
                      />
                      <GenericTag 
                        type="status" 
                        value={mapTodoStatusToTag(selectedTask.status)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-secondary">Assignee</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="h-4 w-4 bg-gray-300 rounded-full" />
                        <span className="table-text">{selectedTask.assignee?.full_name || 'Unassigned'}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-secondary">Due Date</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="h-4 w-4" />
                        <span className="table-text">
                          {selectedTask.due_date ? formatDate(selectedTask.due_date) : 'No due date'}
                        </span>
                      </div>
                      {selectedTask.due_time && (
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-4 w-4" />
                          <span className="table-text">
                            {formatTime(selectedTask.due_time)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Attachments Section (generic architecture, same
                    component every other modal uses) */}
                <GenericAttachmentsSection
                  attachments={taskAttachments[selectedTask.id] || []}
                  onUpload={async (files: File[]) => {
                    if (!companyId || !user?.id) return;
                    await uploadTaskAttachments(selectedTask.id, files, companyId, user.id);
                    fetchTaskAttachments(selectedTask.id);
                  }}
                  onDelete={(attachmentId) => handleDeleteAttachment(selectedTask.id, attachmentId)}
                  mode="edit"
                  projectId={selectedTask.project_id || ''}
                 
                />

                {/* Comments Section (generic architecture, entity_type='task') */}
                <GenericCommentsSection
                  comments={comments}
                  onAddComment={addComment}
                  onDeleteComment={deleteComment}
                  mode="edit"
                  audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
                  placeholder="Add a comment..."
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </SectionLayout>
  );
} 